import { useCallback, useEffect, useState } from "react";
import { Inbox, CheckCircle2, XCircle } from "lucide-react";
import { toast } from "sonner";
import api, { formatApiError } from "../lib/api";
import EventCard from "../components/EventCard";
import { EVENT_TYPES } from "../components/Badges";

const ConfirmQueue = () => {
  const [events, setEvents] = useState([]);
  const [loading, setLoading] = useState(true);
  const [reclassify, setReclassify] = useState({});

  const load = useCallback(async () => {
    try {
      const r = await api.get("/events", { params: { needs_confirm: true } });
      setEvents(r.data);
      const map = {};
      r.data.forEach((e) => (map[e.id] = e.event_type));
      setReclassify(map);
    } catch (e) {
      toast.error(formatApiError(e));
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    load();
  }, [load]);

  const confirm = async (event) => {
    try {
      await api.post(`/events/${event.id}/confirm`, { event_type: reclassify[event.id] || event.event_type });
      toast.success("Confirmed — vendor memory updated");
      load();
    } catch (e) {
      toast.error(formatApiError(e));
    }
  };

  const reject = async (event) => {
    try {
      await api.post(`/events/${event.id}/reject`);
      toast.success("Event rejected");
      load();
    } catch (e) {
      toast.error(formatApiError(e));
    }
  };

  return (
    <div data-testid="confirm-queue-page" className="space-y-5">
      <div className="flex items-center gap-3">
        <div className="flex h-10 w-10 items-center justify-center rounded-lg bg-amber-50 dark:bg-amber-500/10 border border-amber-300 dark:border-amber-500/40">
          <Inbox className="h-5 w-5 text-amber-600 dark:text-amber-400" />
        </div>
        <div>
          <h1 className="text-2xl md:text-3xl font-bold tracking-tight text-slate-900 dark:text-slate-100">Needs your confirm</h1>
          <p className="text-base text-slate-600 dark:text-slate-400">
            Low-confidence AI classifications. Confirming teaches the tower — autonomy is earned per brand.
          </p>
        </div>
      </div>

      {loading ? (
        <div className="flex h-40 items-center justify-center" data-testid="confirm-loading">
          <div className="h-8 w-8 rounded-full border-2 border-emerald-500 dark:border-emerald-500/60 border-t-transparent animate-spin" />
        </div>
      ) : events.length === 0 ? (
        <div data-testid="confirm-queue-empty" className="rounded-lg border border-slate-300 dark:border-slate-700 bg-white dark:bg-slate-900 p-10 text-center">
          <CheckCircle2 className="mx-auto h-8 w-8 text-emerald-600 dark:text-emerald-400 mb-2" />
          <p className="text-base text-slate-600 dark:text-slate-400">Queue clear. Every captured event is confirmed.</p>
        </div>
      ) : (
        <div className="grid grid-cols-1 lg:grid-cols-2 gap-4" data-testid="confirm-queue-list">
          {events.map((e) => (
            <div key={e.id} className="space-y-2">
              <EventCard event={e} onConfirm={confirm} onReject={reject} />
              <div className="flex items-center gap-2 pl-1" data-testid={`reclassify-row-${e.id}`}>
                <span className="text-sm font-mono uppercase tracking-widest text-slate-600 dark:text-slate-400">Re-classify:</span>
                <select
                  data-testid={`reclassify-select-${e.id}`}
                  value={reclassify[e.id] || e.event_type}
                  onChange={(ev) => setReclassify({ ...reclassify, [e.id]: ev.target.value })}
                  onClick={(ev) => ev.stopPropagation()}
                  className="rounded-md border border-slate-300 dark:border-slate-700 bg-white dark:bg-slate-900 px-2 py-1 text-sm text-slate-700 dark:text-slate-300 focus:outline-none"
                >
                  {EVENT_TYPES.map((t) => (
                    <option key={t} value={t}>{t}</option>
                  ))}
                </select>
                <button
                  data-testid={`quick-reject-${e.id}`}
                  onClick={() => reject(e)}
                  className="ml-auto inline-flex items-center gap-1 text-sm text-slate-600 dark:text-slate-400 hover:text-rose-600 dark:hover:text-rose-400 transition-colors"
                >
                  <XCircle className="h-3.5 w-3.5" /> Not an event
                </button>
              </div>
            </div>
          ))}
        </div>
      )}
    </div>
  );
};

export default ConfirmQueue;
